import React, { useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Button,
  Input,
  Row,
  Col,
  Select,
  message,
  Spin,
  Dropdown,
  Menu,
  Tag,
  Popconfirm,
  Modal,
  Space,
  Tooltip,
  Typography,
  Checkbox,
} from "antd";
import TextArea from "antd/lib/input/TextArea";
import { Link } from "react-router-dom";
import {
  SearchOutlined,
  CloseOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { RiAddBoxFill } from "react-icons/ri";
import { FaEdit, FaPen, FaTrashAlt } from "react-icons/fa";
import { AiFillDelete } from "react-icons/ai";
import type { ColumnsType } from "antd/es/table";
import {
  getallovertime,
  getOvertimeById,
  updateOvertime,
  deleteOvertime,
  getallDeveloperForTask,
  getSingleUserAllOverTime,
  getallDeveloper,
} from "../../utils/API";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { AuthContext } from "../../Auth/AuthContext";
import { hasPermission } from "../../utils/HasPermission";
import Editovertimemodal from "./Edit";
import Addovertimemodal from "./Add";
import { autoRegisterCurrentPageColumns } from "../../utils/emailTemplateColumns";

dayjs.extend(utc);
dayjs.extend(timezone);

// Define statusColors mapping
const statusStyles: any = {
  Pending: { background: "#fff3cd", color: "#856404" }, // Warning
  Approved: { background: "#d4edda", color: "#155724" }, // Success
  Rejected: { background: "#f8d7da", color: "#721c24" }, // Danger
};
interface DataType {
  key: React.Key;
  _id: string;
  title: string;
  description: string;
  status: string;
  date: string;
  startTime: string;
  endTime: string;
  hours: string;
  username: string;
  userId: string;
  highlightRed?: boolean;
  remarks?: string;
  serialNumber: number;
}

const OvertimePage: React.FC = () => {
  const { authData } = useContext(AuthContext);
  const navigate = useNavigate();
  const today = dayjs();
  const prevMonth = today.subtract(1, "month");
  // State management
  const [searchText, setSearchText] = useState<string>("");
  const [data, setData] = useState<DataType[]>([]);
  const [loadingDevelopers, setLoadingDevelopers] = useState(false);

  const [loading, setLoading] = useState<boolean>(true);
  const [currentTask, setCurrentTask] = useState<DataType | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [page, setPage] = useState(1);
  const todayDate = new Date().getDate();
  const [selectedDay, setSelectedDay] = useState<number | null>(todayDate);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [selectedTaskRecord, setSelectedTaskRecord] = useState<DataType | null>(
    null,
  );
  const userId = authData?.user?._id;
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [totalRecords, setTotalRecords] = useState(10);
  const [selectedDeveloper, setSelectedDeveloper] = useState<string | null>(
    null,
  );
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [pageSize, setPageSize] = useState(10);
  const [selectedMonthNum, setSelectedMonthNum] = useState<number>(
    today.month() + 1,
  ); // Current month
  const [selectedYear, setSelectedYear] = useState<number>(today.year()); // Current year
  const [developers, setDevelopers] = useState<Developer[]>([]);

  dayjs.extend(utc);
  dayjs.extend(timezone);

  interface Developer {
    _id: string;
    username: string;
  }

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Remarks editing state
  const [remarksModalState, setRemarksModalState] = useState({
    isOpen: false,
    remarks: "",
    recordId: "",
  });

  // Editing state
  const [editingState, setEditingState] = useState({
    isEditing: false,
    editedText: "",
    recordId: "",
    field: "",
  });

  useEffect(() => {
    fetchOvertimeData();
    if (authData) {
      fetchDevelopers();
    }
  }, [
    authData,
    selectedDeveloper,
    selectedMonthNum,
    selectedYear,
    searchText,
    page,
  ]);

  const fetchDevelopers = async () => {
    try {
      setLoadingDevelopers(true);
      const result = await getallDeveloperForTask("", "");
      if (result?.data) {
        setDevelopers(result.data);
      } else {
        message.warning("No developers found");
        setDevelopers([]);
      }
    } catch (error) {
      console.error("Error fetching developers:", error);
      message.error("Failed to fetch developers");
      setDevelopers([]);
    } finally {
      setLoadingDevelopers(false);
    }
  };

  const fetchOvertimeData = async () => {
    try {
      setLoading(true);
      let response: { total?: number; data: any[] };

      const month = selectedMonthNum || 0;
      const year = selectedYear || 0;
      const search = searchText || "";

      if (authData?.roleId?.roleId === 1) {
        response = await getallovertime(search, page, pageSize, {
          month,
          year,
          userId: selectedDeveloper || "",
        });
      } else {
        response = await getSingleUserAllOverTime(userId, page, pageSize, {
          month,
          year,
          search,
        });
      }

      if (response) {
        setTotalRecords(response.total || response.data.length);
      }

      const sortedData = [...response.data].sort((a, b) => {
        const dateA = dayjs(a.date, "DD-MM-YYYY");
        const dateB = dayjs(b.date, "DD-MM-YYYY");

        const dateDiff = dateB.diff(dateA);
        if (dateDiff !== 0) return dateDiff;

        const timeA = dayjs(a.startTime || "00:00", "HH:mm");
        const timeB = dayjs(b.startTime || "00:00", "HH:mm");
        return timeB.diff(timeA);
      });

      const formattedData = sortedData.map((item, index) => {
        const startTime = item.startTime || "00:00";
        const endTime = item.endTime || "00:00";

        const start = dayjs(startTime, "HH:mm");
        const end = dayjs(endTime, "HH:mm");

        let hours = "0.00";
        if (start.isValid() && end.isValid()) {
          const diffMinutes = end.diff(start, "minute");
          hours = (diffMinutes / 60).toFixed(2);
        }

        const formattedDate = dayjs(item.date, [
          "YYYY-MM-DD",
          "DD-MM-YYYY",
          "MM-DD-YYYY",
        ]).isValid()
          ? dayjs(item.date, ["YYYY-MM-DD", "DD-MM-YYYY", "MM-DD-YYYY"]).format(
              "DD-MM-YYYY",
            )
          : "-";

        return {
          key: item._id,
          _id: item._id,
          serialNumber: index + 1,
          title: item.title || "No title",
          description: item.description || "No description",
          status: item.status || "Pending",
          date: formattedDate,
          startTime,
          endTime,
          hours,
          username: item.userId?.username || "Unknown",
          userId: item.userId?._id || item.userId,
          remarks: item.remarks || "",
        };
      });

      setData(formattedData);
    } catch (error) {
      console.error("Error fetching overtime data:", error);
      message.error("Failed to fetch overtime data");
    } finally {
      setLoading(false);
    }
  };

  const updateSelectedMonth = (month: number, year: number) => {
    const monthStr = month < 10 ? `0${month}` : `${month}`;
    setSelectedMonthNum(month);
  };

  const handleEdit = (record: DataType) => {
    setCurrentTask(record);
    setIsEditModalVisible(true);
    fetchOvertimeData();
  };

  const handleDelete = async () => {
    if (!deleteId) {
      return;
    }

    try {
      setDeletingId(deleteId);
      const response = await deleteOvertime(deleteId);
      message.success("Overtime entry deleted successfully!");
      fetchOvertimeData();
    } catch (error: any) {
      console.error("Delete error:", error);
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to delete overtime. Please try again.",
      );
    } finally {
      setDeletingId(null);
      setDeleteId(null);
    }
  };

  const changeStatus = async (userId: string, newStatus: string) => {
    if (!userId || !newStatus) {
      console.error("❌ Invalid Parameters", { userId, newStatus });
      message.error("Invalid overtime ID or status.");
      return;
    }

    try {
      const response = await updateOvertime(userId, { status: newStatus });

      if (
        response?.success ||
        response?.status === 200 ||
        response?.message?.toLowerCase().includes("updated")
      ) {
        message.success("Overtime status updated!");
        fetchOvertimeData(); // Refresh data
      } else {
        message.error(response?.message || "❌ Failed to update status.");
      }
    } catch (error: any) {
      console.error("❌ API Error:", error?.response?.data || error.message);
      message.error(
        error?.response?.data?.message || "Error updating overtime.",
      );
    }
  };

  const handleSaveRemarks = async (id: string) => {
    if (!id) return;

    try {
      const response = await updateOvertime(editingState.recordId, {
        remarks: editingState.editedText,
        recordId: editingState.recordId,
      });

      if (response?.success) {
        message.success("Remarks updated successfully!");
        fetchOvertimeData();
        setEditingState({
          isEditing: false,
          editedText: "",
          recordId: "",
          field: "",
        });
        setRemarksModalState({
          isOpen: false,
          remarks: "",
          recordId: "",
        });
      } else {
        message.error(response?.message || "Failed to update remarks");
      }
    } catch (error) {
      console.error("Error updating remarks:", error);
    }
  };

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRowsData: any[]) => {
      setSelectedRows(selectedRowsData);
    },
  };

  const columns: ColumnsType<DataType> = [
    ...(authData?.roleId?.roleId === 1
      ? [
          {
            title: "",
            key: "selection",
            width: 50,
            render: (_: any, record: { _id: any }) => (
              <Checkbox
                checked={selectedRows.includes(record._id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedRows([...selectedRows, record._id]);
                  } else {
                    setSelectedRows(
                      selectedRows.filter((id) => id !== record._id),
                    );
                  }
                }}
              />
            ),
          },
        ]
      : []),

    {
      title: "#",
      key: "index",
      render: (_text, _record, index) => index + 1,
    },
    {
      title: "Name",
      dataIndex: "username",
      key: "username",
      render: (text: string) => text || "-",
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text: string) => {
        const words = text.trim().split(/\s+/);
        const isLong = words.length > 2;
        const shortTitle = isLong ? `${words.slice(0, 2).join(" ")}...` : text;

        const content = (
          <div className="text-gray-800 hover:text-black ">{shortTitle}</div>
        );

        return isLong ? (
          <Tooltip
            title={text}
            placement="topLeft"
            color="#CCCCCC"
            overlayInnerStyle={{
              color: "#000", // ✅ font color
              padding: "12px",
            }}
          >
            <div className="flex items-start gap-2 text-gray-700 hover:text-black cursor-pointer">
              {content}
            </div>
          </Tooltip>
        ) : (
          content
        );
      },
    },
    {
      title: "Task Description",
      dataIndex: "description",
      key: "description",
      width: 180,
      render: (text: string) => {
        const shortPreview =
          text.length > 40 ? `${text.substring(0, 40)}...` : text;

        const content = (
          <Typography.Paragraph
            ellipsis={{ rows: 2, expandable: false }}
            className="!mb-0 !text-sm max-w-[200px]"
          >
            {shortPreview}
          </Typography.Paragraph>
        );

        if (text.length > 40) {
          return (
            <Tooltip
              title={
                <div className="max-w-xs whitespace-pre-line text-sm">
                  {text}
                </div>
              }
              placement="topLeft"
              overlayInnerStyle={{
                color: "#000",
                whiteSpace: "pre-line",
                padding: "12px",
              }}
              color="#CCCCCC"
            >
              <div className="flex items-start gap-2 text-gray-700 hover:text-black cursor-pointer">
                {content}
              </div>
            </Tooltip>
          );
        }

        return (
          <div className="flex items-start gap-2 text-gray-700 hover:text-black">
            {content}
          </div>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (text: string) => (
        <span style={{ color: "#6B7280", fontWeight: 500 }}>{text || "-"}</span>
      ),
    },
    { title: "Start Time", dataIndex: "startTime", key: "startTime" },
    { title: "End Time", dataIndex: "endTime", key: "endTime" },
    {
      title: "Hours",
      dataIndex: "hours",
      key: "hours",
      render: (hours: string | number) => {
        const totalMinutes = Math.round(parseFloat(hours as string) * 60);
        const hrs = Math.floor(totalMinutes / 60);
        const mins = totalMinutes % 60;

        if (hrs === 0 && mins === 0) {
          return (
            <Tag
              style={{
                background: "linear-gradient(135deg, #d3cce3, #e9e4f0)",
                color: "#333",
                padding: "0px 10px",
                borderRadius: "16px",
                fontSize: "12px",
                border: "none",
              }}
            >
              0 hrs
            </Tag>
          );
        }

        const hrStr = hrs > 0 ? `${hrs} hr${hrs > 1 ? "s" : ""}` : "";
        const minStr = mins > 0 ? `${mins} min${mins > 1 ? "s" : ""}` : "";
        const display = [hrStr, minStr].filter(Boolean).join(" ");

        return (
          <Tag
            style={{
              background: "linear-gradient(135deg, #56CCF2, #2F80ED)",
              color: "#fff",
              padding: "2px 8px",
              borderRadius: "5px",
              fontSize: "12px",
              border: "none",
            }}
          >
            {display}
          </Tag>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) =>
        authData?.roleId?.roleId === 1 ? (
          <Dropdown
            overlay={
              <Menu
                onClick={({ key }: { key: string }) =>
                  changeStatus(record._id, key)
                }
              >
                {["Pending", "Approved", "Rejected"].map((item) => (
                  <Menu.Item key={item}>
                    <span
                      className="badge rounded-pill"
                      style={{
                        backgroundColor: statusStyles[item]?.background,
                        color: statusStyles[item]?.color,
                      }}
                    >
                      {item}
                    </span>
                  </Menu.Item>
                ))}
              </Menu>
            }
            trigger={["click"]}
          >
            <span
              className="badge rounded-pill"
              style={{
                backgroundColor: statusStyles[record.status]?.background,
                color: statusStyles[record.status]?.color,
                cursor: "pointer",
              }}
            >
              {record.status}
            </span>
          </Dropdown>
        ) : (
          <span
            className="badge rounded-pill"
            style={{
              backgroundColor: statusStyles[record.status]?.background,
              color: statusStyles[record.status]?.color,
            }}
          >
            {record.status}
          </span>
        ),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      render: (text: string, record: DataType) => {
        return (
          <span
            onClick={() => {
              setRemarksModalState({
                isOpen: true,
                remarks: record.remarks || "",
                recordId: record._id,
              });
            }}
            style={{ cursor: "pointer" }}
          >
            {text?.length > 40 ? `${text.substring(0, 40)}...` : text || "-"}
          </span>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_: any, record: DataType) => (
        <div className="d-flex" style={{ gap: "8px", alignItems: "center" }}>
          {hasPermission("projects", "update") && (
            <Tooltip title="Edit">
              <button
                onClick={() => handleEdit(record)}
                className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 
               shadow-[0_4px_12px_rgba(139,92,246,0.3)] 
               hover:shadow-[0_6px_16px_rgba(139,92,246,0.4)] 
               active:translate-y-[1px] active:shadow-none 
               text-white flex items-center justify-center 
               transition-all duration-200 ease-in-out group"
              >
                <FaPen
                  className="group-hover:scale-110 transition-transform duration-200"
                  size={12}
                />
              </button>
            </Tooltip>
          )}
          {hasPermission("projects", "delete") && (
            <Tooltip
              title={deletingId === record._id ? "Deleting..." : "Delete"}
              placement="top"
            >
              <button
                onClick={() => {
                  if (deletingId !== record._id) {
                    setDeleteId(record._id);
                  }
                }}
                disabled={deletingId === record._id}
                className={`w-7 h-7 rounded-full flex items-center justify-center text-white transition-all duration-200 group
      ${
        deletingId === record._id
          ? "bg-gray-100 cursor-not-allowed"
          : "bg-gradient-to-br from-rose-500 to-rose-700 shadow-[0_4px_12px_rgba(244,63,94,0.3)] hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)] active:translate-y-1 active:shadow-none cursor-pointer"
      }`}
              >
                {deletingId === record._id ? (
                  <Spin
                    indicator={
                      <LoadingOutlined
                        style={{ color: "#9ca3af", fontSize: 12 }}
                        spin
                      />
                    }
                  />
                ) : (
                  <FaTrashAlt
                    className="group-hover:scale-110 transition-transform"
                    size={12}
                  />
                )}
              </button>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    autoRegisterCurrentPageColumns(columns);
  }, []);

  return (
    <div>
      {/* Header & Search */}
      <div className="overflow-x-auto w-full">
        <Row
          gutter={[16, 16]}
          align="middle"
          justify="space-between"
          wrap={false}
          className="min-w-[1000px] m-2"
        >
          {/* Left - Heading */}
          <Col flex="none">
            <h2 className="headername">Overtime</h2>
          </Col>

          {/* Center - Developer Select and Month/Year */}
          <Col flex="auto" style={{ minWidth: 300 }}>
            <div className="flex justify-center items-center gap-2 w-full">
              {authData?.roleId?.roleId === 1 && (
                <Select
                  showSearch
                  style={{ width: "40%", minWidth: 150, height: "33px" }}
                  placeholder="Select developer"
                  value={selectedDeveloper}
                  onChange={(value: string) => {
                    setSelectedDeveloper(value || null);
                    setPage(1);
                  }}
                  allowClear
                  onClear={() => {
                    setSelectedDeveloper(null);
                    setPage(1);
                  }}
                >
                  {developers.map((dev) => (
                    <Select.Option key={dev._id} value={dev._id}>
                      {dev.username}
                    </Select.Option>
                  ))}
                </Select>
              )}

              <Select
                value={selectedMonthNum}
                style={{ width: "100%", maxWidth: 150, height: "33px" }}
                allowClear
                onChange={(value) => {
                  setSelectedMonthNum(value);
                  updateSelectedMonth(value, selectedYear);
                  setPage(1);
                }}
                options={[
                  { value: 1, label: "January" },
                  { value: 2, label: "February" },
                  { value: 3, label: "March" },
                  { value: 4, label: "April" },
                  { value: 5, label: "May" },
                  { value: 6, label: "June" },
                  { value: 7, label: "July" },
                  { value: 8, label: "August" },
                  { value: 9, label: "September" },
                  { value: 10, label: "October" },
                  { value: 11, label: "November" },
                  { value: 12, label: "December" },
                ]}
              />

              <Select
                value={selectedYear}
                style={{ width: "100%", maxWidth: 150, height: "33px" }}
                onChange={(value) => {
                  setSelectedYear(value);
                  updateSelectedMonth(selectedMonthNum, value);
                  setPage(1);
                }}
                options={Array.from({ length: 20 }, (_, i) => {
                  const year = dayjs().year() - 10 + i;
                  return { value: year, label: year.toString() };
                })}
              />
            </div>
          </Col>

          {/* Right - Search and Buttons */}
          <Col flex="none">
            <div className="flex justify-end items-center gap-2">
              <Input
                prefix={<SearchOutlined />}
                size="large"
                placeholder="Search..."
                allowClear
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setPage(1);
                }}
                style={{ width: "100%", maxWidth: 170, height: "33px" }}
              />

              {authData?.roleId?.roleId !== 1 && (
                <>
                  <Button
                    type="primary"
                    onClick={() => setIsAddTaskModalOpen(true)}
                    className="allbuttons"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    <RiAddBoxFill style={{ fontSize: "18px" }} />
                    <div className="ms-2">Add</div>
                  </Button>

                  <Addovertimemodal
                    visible={isAddTaskModalOpen}
                    onClose={() => setIsAddTaskModalOpen(false)}
                    onTaskCreated={() => {
                      setIsAddTaskModalOpen(false);
                      fetchOvertimeData();
                    }}
                  />
                </>
              )}

              {authData?.roleId?.roleId === 1 && (
                <button
                  className="bg-[#001529] text-white w-40 p-2 rounded text-center btn flex items-center justify-center"
                  disabled={selectedRows.length === 0}
                  onClick={async () => {
                    // Update statuses in parallel for better performance
                    await Promise.all(
                      selectedRows.map((id) => changeStatus(id, "Approved")),
                    );
                  }}
                  style={{
                    pointerEvents: "auto",
                    backgroundColor: "#001529",
                    border: "none",
                    boxShadow: "none",
                    outline: "none",
                    whiteSpace: "nowrap",
                    height: "33px",
                  }}
                >
                  <div className="ms-2"> Approve</div>
                </button>
              )}
            </div>
          </Col>
        </Row>
      </div>

      <Modal
        open={!!deleteId} // <-- This makes the modal open when deleteId has a value
        onOk={handleDelete}
        onCancel={() => setDeleteId(null)}
        okText="Yes"
        cancelText="No"
        // footer={null}
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to delete this Developer?</p>
      </Modal>

      {/* Table Section */}
      <Row>
        <Card className="w-100">
          <Col>
            <Spin spinning={loading}>
              <div
                className="scrollbar-hide custom-scroll-table"
                style={{ overflowX: "auto" }}
              >
                <Table
                  columns={columns}
                  dataSource={data}
                  rowKey="_id"
                  scroll={{ x: "max-content" }}
                  pagination={{
                    pageSize: pageSize,
                    current: page,
                    onChange: (pageNumber) => setPage(pageNumber),
                    showSizeChanger: false,
                    total: totalRecords,
                  }}
                />
              </div>
            </Spin>
          </Col>
        </Card>
      </Row>

      <div>
        {/* Task Description Modal */}
        <Modal
          title={null}
          open={taskModalOpen}
          onCancel={() => setTaskModalOpen(false)}
          width={500}
          footer={null}
          centered
          closeIcon={false}
          className="custom-modal"
        >
          <div className="flex items-center justify-between px-2 py-2 border-b border-gray-200">
            <span className="text-base font-semibold text-gray-800">
              Task Description
            </span>
            <CloseOutlined
              className="modal-close-icon text-gray-500 hover:text-gray-700 text-sm cursor-pointer"
              onClick={() => setTaskModalOpen(false)}
            />
          </div>

          <div className="px-6 py-4 text-gray-700 text-sm">
            <p>
              {selectedTaskRecord?.description || "No description available."}
            </p>
          </div>

          <div className="w-full border-t border-gray-200 px-6 py-2" />
        </Modal>

        {/* Updated Remarks Modal */}
        <Modal
          title="Remarks"
          open={remarksModalState.isOpen}
          onCancel={() =>
            setRemarksModalState({ ...remarksModalState, isOpen: false })
          }
          footer={null}
          styles={{
            header: {
              borderBottom: "1px solid #e0e0e0",
              padding: "12px",
              position: "sticky",
              top: 0,
              backgroundColor: "white",
            },
            content: {
              padding: 0,
              overflow: "hidden",
            },
            body: {
              padding: "0px",
            },
          }}
        >
          {authData?.roleId?.roleId === 1 ? (
            <>
              <div style={{ padding: "8px" }}>
                <TextArea
                  rows={4}
                  value={remarksModalState.remarks}
                  onChange={(e) =>
                    setRemarksModalState({
                      ...remarksModalState,
                      remarks: e.target.value,
                    })
                  }
                  placeholder="Enter remarks here..."
                  style={{ marginTop: 16 }}
                />
              </div>

              <Row
                justify="end"
                style={{
                  marginTop: 16,
                  paddingTop: 18,
                  padding: "11px",
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                <Col>
                  <Button
                    onClick={() =>
                      setRemarksModalState({
                        ...remarksModalState,
                        isOpen: false,
                      })
                    }
                    style={{
                      marginRight: 8,
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    style={{
                      backgroundColor: "#001529",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                    }}
                    onClick={() => {
                      setEditingState({
                        isEditing: true,
                        editedText: remarksModalState.remarks,
                        recordId: remarksModalState.recordId,
                        field: "remarks",
                      });
                      handleSaveRemarks(remarksModalState.recordId);
                    }}
                  >
                    Save
                  </Button>
                </Col>
              </Row>
            </>
          ) : (
            <p style={{ whiteSpace: "pre-wrap", marginTop: 16 }}>
              {remarksModalState.remarks || "No remarks available."}
            </p>
          )}
        </Modal>
      </div>

      {currentTask && (
        <Editovertimemodal
          visible={isEditModalVisible}
          taskId={currentTask._id}
          onClose={() => setIsEditModalVisible(false)}
          onSuccess={() => {
            setIsEditModalVisible(false);
            fetchOvertimeData();
          }}
        />
      )}
    </div>
  );
};

export const OVERTIME_TABLE_COLUMNS = [
  "username",
  "title",
  "description",
  "date",
  "startTime",
  "endTime",
  "hours",
  "status",
  "remarks",
];

export default OvertimePage;
